import { useState, useMemo } from "react";
import type { LateralCandidate } from "@/types/database";
import type { LateralJobPosition } from "@/types/database";
import {
  LateralFilterValues,
  emptyLateralFilters,
} from "@/components/lateral/LateralFilterBar";

export type LateralCandidateRow = LateralCandidate & {
  lateral_job_positions?: { job_title: string } | null;
};

export type SortField =
  | "full_name"
  | "cv_score"
  | "total_experience_years"
  | "created_at"
  | "expected_ctc";

export function useLateralListFilters(candidates: LateralCandidateRow[] | undefined) {
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState<LateralFilterValues>(emptyLateralFilters());
  const [sortBy, setSortBy] = useState<SortField>("created_at");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  const setFilter = (key: keyof LateralFilterValues, value: string) =>
    setFilters((f) => ({ ...f, [key]: value }));

  const clearFilters = () => {
    setSearch("");
    setFilters(emptyLateralFilters());
  };

  const handleSort = (field: SortField) => {
    if (sortBy === field) setSortOrder((o) => (o === "asc" ? "desc" : "asc"));
    else { setSortBy(field); setSortOrder("desc"); }
  };

  const filtered = useMemo(() => {
    let rows = candidates ?? [];

    // Text search
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      rows = rows.filter(
        (c) =>
          c.full_name.toLowerCase().includes(q) ||
          (c.email ?? "").toLowerCase().includes(q) ||
          (c.current_company ?? "").toLowerCase().includes(q) ||
          (c.current_designation ?? "").toLowerCase().includes(q) ||
          (c.current_location ?? "").toLowerCase().includes(q)
      );
    }

    // Filter panel values
    const f = filters;
    if (f.statusFilter !== "all") rows = rows.filter((c) => c.status === f.statusFilter);
    if (f.positionFilter !== "all") rows = rows.filter((c) => c.lateral_job_position_id === f.positionFilter);
    if (f.minScore !== "") rows = rows.filter((c) => (c.cv_score ?? 0) >= Number(f.minScore));
    if (f.maxScore !== "") rows = rows.filter((c) => (c.cv_score ?? 0) <= Number(f.maxScore));
    if (f.minExp !== "") rows = rows.filter((c) => (c.total_experience_years ?? 0) >= Number(f.minExp));
    if (f.maxExp !== "") rows = rows.filter((c) => (c.total_experience_years ?? 0) <= Number(f.maxExp));
    if (f.locationFilter.trim()) {
      const q = f.locationFilter.trim().toLowerCase();
      rows = rows.filter((c) => (c.current_location ?? "").toLowerCase().includes(q));
    }
    if (f.noticePeriodFilter.trim()) {
      const q = f.noticePeriodFilter.trim().toLowerCase();
      rows = rows.filter((c) => (c.notice_period ?? "").toLowerCase().includes(q));
    }

    // Sort
    rows = [...rows].sort((a, b) => {
      let av: any, bv: any;
      switch (sortBy) {
        case "full_name":          av = a.full_name.toLowerCase(); bv = b.full_name.toLowerCase(); break;
        case "cv_score":           av = a.cv_score ?? -1;          bv = b.cv_score ?? -1;          break;
        case "total_experience_years": av = a.total_experience_years ?? -1; bv = b.total_experience_years ?? -1; break;
        case "created_at":         av = a.created_at;              bv = b.created_at;              break;
        case "expected_ctc":       av = a.expected_ctc ?? "";      bv = b.expected_ctc ?? "";      break;
      }
      if (av < bv) return sortOrder === "asc" ? -1 : 1;
      if (av > bv) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });

    return rows;
  }, [candidates, search, filters, sortBy, sortOrder]);

  const hasActiveFilters =
    search.trim() !== "" ||
    filters.statusFilter !== "all" ||
    filters.positionFilter !== "all" ||
    filters.minScore !== "" ||
    filters.maxScore !== "" ||
    filters.minExp !== "" ||
    filters.maxExp !== "" ||
    filters.locationFilter.trim() !== "" ||
    filters.noticePeriodFilter.trim() !== "";

  const activeFilterCount = [
    search.trim() !== "",
    filters.statusFilter !== "all",
    filters.positionFilter !== "all",
    filters.minScore !== "" || filters.maxScore !== "",
    filters.minExp !== "" || filters.maxExp !== "",
    filters.locationFilter.trim() !== "",
    filters.noticePeriodFilter.trim() !== "",
  ].filter(Boolean).length;

  return {
    search, setSearch,
    filters, setFilter,
    clearFilters,
    filtered,
    hasActiveFilters,
    activeFilterCount,
    sortBy, sortOrder, handleSort,
  };
}
